import {
  ButtonHTMLAttributes,
  ReactElement,
  ReactNode,
  RefObject,
  SVGProps,
} from 'react';
import React from 'react';
import { type VariantProps, tv } from 'tailwind-variants';

import Loading from '../atoms/Loading';

const button = tv({
  base: 'focus-visible relative inline-flex items-center justify-center rounded-lg text-center transition-all duration-200 ease-in-out hover:no-underline',
  variants: {
    variant: {
      primary: 'bg-blue text-white',
      'primary-destructive': 'bg-red text-white',
      secondary: 'bg-gray-15 text-gray-90 dark:bg-gray-75 dark:text-gray-15',
      'secondary-destructive': 'bg-gray-15 text-red dark:bg-gray-75',
      tertiary:
        'text-gray-90 data-[state=open]:bg-gray-15 data-[state=open]:dark:bg-gray-75 dark:text-white',
      'tertiary-destructive':
        'data-[state=open]:bg-gray-15 data-[state=open]:dark:bg-gray-75 text-red',
      overlay:
        'text-gray-90 dark:bg-gray-90/35 dark:hover:bg-gray-90 bg-gray-15/35 hover:bg-gray-15 dark:text-white',
    },
    size: {
      sm: 'h-[30px] gap-[3px]',
      lg: 'h-10 gap-2',
    },
    padding: {
      'text-only': 'px-4',
      'icon-only': 'px-2.5',
      'icon-start': 'pr-4 pl-3',
      'icon-end': 'pr-3 pl-4',
    },
    disabled: {
      true: 'text-gray-90 bg-gray-30 cursor-not-allowed opacity-50',
      false: 'cursor-pointer',
    },
    loading: {
      true: 'cursor-default',
      false: '',
    },
  },
  compoundVariants: [
    // hover styles
    {
      variant: 'primary',
      loading: false,
      disabled: false,
      className: 'cursor-pointer hover:bg-[#166ADC] active:scale-[0.98]',
    },
    {
      variant: 'primary-destructive',
      loading: false,
      disabled: false,
      className: 'cursor-pointer hover:bg-[#DC3A4E] active:scale-[0.98]',
    },
    {
      variant: ['secondary', 'secondary-destructive'],
      loading: false,
      disabled: false,
      className:
        'hover:bg-gray-30 dark:hover:bg-gray-60 cursor-pointer active:scale-[0.98]',
    },
    {
      variant: ['tertiary', 'tertiary-destructive'],
      loading: false,
      disabled: false,
      className:
        'hover:bg-gray-30 dark:hover:bg-gray-75 cursor-pointer active:scale-[0.98]',
    },
    // padding styles
    {
      size: 'sm',
      padding: 'text-only',
      className: 'p-3',
    },
    {
      size: 'sm',
      padding: 'icon-start',
      className: 'pr-3 pl-[5px]',
    },
    {
      size: 'sm',
      padding: 'icon-end',
      className: 'pr-[5px] pl-3',
    },
    {
      size: 'sm',
      padding: 'icon-only',
      className: 'p-[5px]',
    },
    // small
    {
      variant: ['secondary', 'secondary-destructive'],
      size: 'sm',
      className:
        'border-gray-30 dark:bg-gray-90 dark:border-gray-75 data-[state=open]:bg-gray-30 data-[state=open]:dark:bg-gray-75 border bg-white',
    },
    // other
    {
      variant: ['secondary-destructive', 'tertiary-destructive'],
      loading: true,
      className: 'text-gray-90 dark:text-gray-15',
    },
    {
      variant: ['secondary-destructive', 'tertiary-destructive'],
      disabled: true,
      className: 'text-gray-90 dark:text-gray-15',
    },
    {
      variant: ['tertiary', 'tertiary-destructive'],
      disabled: true,
      className: 'bg-transparent',
    },
  ],
  defaultVariants: {
    variant: 'secondary',
    size: 'lg',
    disabled: false,
    loading: false,
  },
});

/**
 * Cartwheel's basic button component.
 * @see {@link https://studio.getcartwheel.com/ui?selected=NewButtonShowcase}
 */
export default function Button({
  children,
  className,
  disabled = false,
  size,
  variant,
  icon,
  alignIcon = 'start',
  loading,
  ref,
  ...props
}: {
  variant?: VariantProps<typeof button>['variant'];
  size?: VariantProps<typeof button>['size'];
  loading?: boolean;
  icon?: ReactElement<SVGProps<SVGSVGElement>>;
  alignIcon?: 'start' | 'end';
  ref?: RefObject<HTMLButtonElement | null>;
  color?: never;
} & ButtonHTMLAttributes<HTMLButtonElement>) {
  const padding = getPadding(icon, alignIcon, children);

  return (
    <button
      className={button({
        variant,
        size,
        disabled,
        loading,
        padding,
        className,
      })}
      ref={ref}
      disabled={disabled}
      tabIndex={disabled || loading ? -1 : 0}
      {...props}
    >
      {loading && (
        <span className="absolute inset-0 flex items-center justify-center">
          <Loading className={size === 'sm' ? 'h-4 w-4' : 'h-5 w-5'} />
        </span>
      )}
      {icon && alignIcon === 'start' && (
        <Icon icon={icon} size={size} loading={loading} />
      )}
      {children ? (
        <span
          className={`truncate pt-[0.15em] font-medium ${loading ? 'opacity-0' : ''} ${size === 'sm' ? 'text-[12px]/[12px]' : 'text-[14px]/[14px]'}`}
        >
          {children}
        </span>
      ) : null}

      {icon && alignIcon === 'end' && (
        <Icon icon={icon} size={size} loading={loading} />
      )}
    </button>
  );
}

function Icon({
  icon,
  size,
  loading,
}: {
  icon: ReactElement<SVGProps<SVGSVGElement>>;
  size: VariantProps<typeof button>['size'];
  loading?: boolean;
}) {
  return (
    <span className={`flex-none ${loading ? 'opacity-0' : ''}`}>
      {React.cloneElement(icon, {
        className: `${icon.props.className ?? ''} ${size === 'sm' ? 'w-4 h-4 mx-0.5' : 'w-5 h-5'}`,
      })}
    </span>
  );
}

function getPadding(
  icon: ReactNode,
  alignIcon: 'start' | 'end',
  children: ReactNode,
) {
  if (icon && !children) return 'icon-only';
  if (icon && alignIcon === 'start') return 'icon-start';
  if (icon && alignIcon === 'end') return 'icon-end';
  return 'text-only';
}
